import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { COLOR_PRESETS } from '@/lib/constants'
import { validateAndToast, createTagSchema } from '@/lib/schemas'
import { useCreateTag, useUpdateTag } from '@/lib/hooks'
import type { Tag } from '@/lib/hooks'
import { fetchTagUsage } from '@/lib/api'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag: Tag | null
  onDelete: (tag: Tag) => void
}

export function TagEditSheet({ open, onOpenChange, tag, onDelete }: Props) {
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()

  const [name, setName] = useState('')
  const [color, setColor] = useState<string>(COLOR_PRESETS[0])

  useEffect(() => {
    if (!open) return
    if (tag) {
      setName(tag.name)
      setColor(tag.color)
    } else {
      setName('')
      setColor(COLOR_PRESETS[0])
    }
  }, [open, tag])

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['tag-usage', tag?.id],
    queryFn: () => fetchTagUsage(tag!.id),
    enabled: open && tag !== null,
    staleTime: 0,
  })

  const handleSave = () => {
    const payload = validateAndToast(createTagSchema, { name, color })
    if (!payload) return

    if (tag) {
      updateTag.mutate(
        { id: tag.id, data: payload },
        { onSuccess: () => onOpenChange(false) },
      )
    } else {
      createTag.mutate(payload, {
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const isPending = createTag.isPending || updateTag.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[85vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>{tag ? 'タグを編集' : 'タグを作成'}</SheetTitle>
          <SheetDescription>
            {tag
              ? 'タグの名前と色を変更できます。'
              : 'カテゴリを横断して取引をグルーピングするためのタグを作成します。'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="tag-name">名前</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 沖縄旅行2025夏"
              autoCapitalize="off"
              enterKeyHint="done"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() && !isPending) handleSave()
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>色</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`size-9 rounded-full border-2 transition-transform hover:scale-110 ${
                    color === preset ? 'border-foreground' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: preset }}
                  onClick={() => setColor(preset)}
                  aria-label={`色: ${preset}`}
                />
              ))}
            </div>
          </div>

          {tag && (
            <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
              {usageLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  <span>使用状況を確認中...</span>
                </div>
              ) : usage ? (
                usage.count > 0 ? (
                  <p>
                    このタグは <span className="font-medium text-foreground">{usage.count}件</span> の取引で使用されています。
                  </p>
                ) : (
                  <p>このタグはまだ使われていません。</p>
                )
              ) : (
                <p>使用状況を取得できませんでした。</p>
              )}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={isPending || !name.trim()}
          >
            {isPending ? '保存中...' : '保存'}
          </Button>
          {tag && (
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => {
                onDelete(tag)
                onOpenChange(false)
              }}
            >
              削除
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
